import { useState, useEffect, useCallback, useRef } from 'react';

interface WakeLockState {
  isSupported: boolean;
  isActive: boolean;
  request: () => Promise<boolean>;
  release: () => Promise<void>;
  error: string | null;
}

export const useWakeLock = (): WakeLockState => {
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  // Ce que l'écran a demandé : survit aux libérations faites par le navigateur
  // (page masquée, écran verrouillé), pour reprendre le verrou au retour
  const wantedRef = useRef(false);

  // Vérification du support
  const isSupported = 'wakeLock' in navigator;

  const request = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Wake Lock not supported');
      return false;
    }

    wantedRef.current = true;
    if (wakeLockRef.current) return true;

    try {
      const sentinel = await navigator.wakeLock.request('screen');
      sentinel.addEventListener('release', () => {
        // Un ancien verrou libéré ne doit pas effacer le verrou courant
        if (wakeLockRef.current === sentinel) {
          wakeLockRef.current = null;
          setIsActive(false);
        }
      });

      // Libéré pendant la demande, ou déjà obtenu par une demande concurrente :
      // on rend aussitôt ce verrou-ci
      if (!wantedRef.current || wakeLockRef.current) {
        await sentinel.release();
        return wakeLockRef.current !== null;
      }

      wakeLockRef.current = sentinel;
      setIsActive(true);
      setError(null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    }
  }, [isSupported]);

  const release = useCallback(async (): Promise<void> => {
    wantedRef.current = false;
    const sentinel = wakeLockRef.current;
    if (sentinel) {
      wakeLockRef.current = null;
      setIsActive(false);
      try {
        await sentinel.release();
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Release failed');
      }
    }
  }, []);

  // Auto-release lors du démontage
  useEffect(() => {
    return () => {
      wantedRef.current = false;
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
      }
    };
  }, []);

  // Le navigateur libère le verrou quand la page est masquée : on le reprend au retour
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && wantedRef.current && !wakeLockRef.current) {
        request();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [request]);

  return {
    isSupported,
    isActive,
    request,
    release,
    error
  };
};
